import { type JSX, useCallback, useEffect, useMemo, useRef } from 'react';
import { Button } from '../Button';
import { Toast, type ToastProps } from 'primereact/toast';
import styles from './Notification.module.scss';
import 'primeicons/primeicons.css';
import InfoIcon from '../../../assets/Icons/Notification/info-icon.svg';
import SuccessIcon from '../../../assets/Icons/Notification/success-icon.svg';
import WarningIcon from '../../../assets/Icons/Notification/warning-icon.svg';
import ErrorIcon from '../../../assets/Icons/Notification/error-icon.svg';

/**
 * PlacementType defines the various placement positions for the notification.
 */
type PlacementType =
  | 'center'
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

/**
 * NotificationType defines the various types of notification.
 */
type NotificationType = 'info' | 'success' | 'warn' | 'error';

/**
 * NotificationState defines the various states of notification.
 */
type NotificationState = 'default' | 'hover';

/**
 * Props for the Notification component.
 */
interface NotificationProps {
  id: string;
  type: NotificationType;
  state: NotificationState;
  title: string;
  description?: boolean;
  descriptionText?: string;
  showCloseIcon?: boolean;
  showCtaButton?: boolean;
  ctaBtnText?: string;
  ctaClick: () => void;
  autocloseDuration?: number;
  placement: PlacementType;
  showButtonPointer?: boolean;
  toastProps?: Partial<ToastProps>;
}

/**
 * Stores registered notifications by ID.
 * Each entry maps an ID to its `showNotification` trigger function.
 */
const toastMap = new Map<string, () => void>();

/**
 * (For standalone application) Triggers a notification by ID if it is registered.
 * @param id - Unique notification ID to trigger
 * @returns {void} Nothing is returned.
 */
export function triggerNotification(id: string): void {
  const triggerToast = toastMap.get(id);
  if (!triggerToast) {
    return;
  }
  triggerToast();
}

/**
 * Custom Notification Component using PrimeReact's Toast.
 *
 * ## Import path
 * ```tsx
 * import { Notification, triggerNotification } from '@kpmg-global-audit-emu/react-ui-kit/components/Notification';
 * ```
 * @param props - Component props
 * @returns {JSX.Element} The rendered Notification component
 */
export const Notification = ({ ...props }: NotificationProps): JSX.Element => {
  const {
    id,
    type,
    state,
    title,
    description,
    descriptionText,
    showCloseIcon,
    showCtaButton,
    ctaBtnText,
    ctaClick,
    autocloseDuration,
    placement,
    showButtonPointer,
    toastProps,
  } = props;
  const toastRef = useRef<Toast>(null);

  const clearNotification = () => {
    toastRef.current?.clear();
  };

  const iconMap = useMemo(
    () => ({
      info: <img src={InfoIcon} alt="info icon" />,
      success: <img src={SuccessIcon} alt="success icon" />,
      warn: <img src={WarningIcon} alt="warning icon" />,
      error: <img src={ErrorIcon} alt="error icon" />,
    }),
    []
  );

  const showNotification = useCallback(() => {
    clearNotification();
    toastRef.current?.show({
      severity: type,
      summary: title,
      detail: description ? descriptionText : '',
      sticky: showCloseIcon
        ? true // close icon present → never autoclose
        : showCtaButton
          ? true // CTA button present → sticky, user dismisses manually
          : false, // no close mechanism → autoclose via `life`
      life: !showCloseIcon && !showCtaButton ? autocloseDuration : undefined,
      icon: iconMap[type],
    });
  }, [
    type,
    title,
    description,
    descriptionText,
    showCloseIcon,
    showCtaButton,
    autocloseDuration,
    iconMap,
  ]);

  // On mount, register this showNotification so triggerNotification() can call it
  useEffect(() => {
    toastMap.set(id, showNotification);
    return () => {
      toastMap.delete(id);
    };
  }, [id, showNotification]);

  return (
    <div className={`${styles.notification}`}>
      <Toast
        ref={toastRef}
        position={toastProps?.position ?? placement}
        className={[
          styles.toast,
          styles[type],
          styles[state],
          placement,
          styles[showCloseIcon ? 'icon-closable' : 'text-closable'],
          styles[!showCloseIcon && !showCtaButton ? 'autoclose' : 'closable'],
          toastProps?.className,
        ]
          .filter(Boolean)
          .join(' ')}
        pt={{
          message: { className: styles.message },
          content: { className: styles.content },
          text: { className: styles['notification-text'] },
          summary: { className: styles.title },
          detail: { className: styles.description },
          closeButton: {
            className: styles['close-button'],
            onClick: () => {
              ctaClick();
              clearNotification();
            },
            'data-label': ctaBtnText,
          },
          ...toastProps?.pt,
        }}
        appendTo={toastProps?.appendTo ?? document.body}
        {...toastProps}
      />
      {showButtonPointer && (
        <Button
          buttonType="Secondary"
          classType="Standard"
          label="Show Notification"
          onClick={showNotification}
          size="Medium"
          state="Default"
          text
          theme="Rectangular"
        />
      )}
    </div>
  );
};
